import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeading from '@/components/feature/SectionHeading';
import { siteInfo } from '@/mocks/categories';
import { stories } from '@/mocks/stories';
import { testimonials } from '@/mocks/testimonials';
import { milestones, principles, team } from '@/mocks/about';

const toneMap: Record<string, { bg: string; text: string }> = {
  primary: { bg: 'bg-primary-100', text: 'text-primary-700' },
  accent: { bg: 'bg-accent-100', text: 'text-accent-700' },
  secondary: { bg: 'bg-secondary-100', text: 'text-secondary-900' },
};

export default function About() {
  usePageMeta({
    title: '关于我们 | 瓜田笔记 · 高校吃瓜趣闻分享站',
    description:
      '了解瓜田笔记的定位与内容原则：我们记录高校吃瓜趣闻与校园日常，坚持模糊化个人信息、不指向具体高校，只分享真实、轻松、带点温度的校园故事。',
    keywords: '关于瓜田笔记,高校吃瓜分享,校园故事平台,内容原则',
    path: '/about',
  });

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于瓜田笔记',
      inLanguage: 'zh-CN',
      description: '瓜田笔记的定位、发展理念与内容原则。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      slogan: siteInfo.slogan,
      email: siteInfo.email,
      description: siteInfo.description,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${window.location.origin}/about` },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={structuredData} />

      <section className="border-b border-background-200 bg-background-100/50">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-chat-smile-2-line" />
                关于 {siteInfo.name}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                把校园里的小事，认真讲给你听
              </h1>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteInfo.name} 是一个专注高校吃瓜趣闻与日常分享的内容站。我们不追热点、不制造对立，
                只做一件事：把宿舍、课堂、社团、食堂里那些真实发生过的瞬间，用舒服的节奏记录下来。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/stories"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  开始围观
                </Link>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  联系我们
                </a>
              </div>
            </div>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=flat%20illustration%20of%20a%20warm%20shared%20study%20room%20with%20notebooks%2C%20tea%20cups%20and%20a%20cork%20board%20of%20campus%20photos%2C%20terracotta%20orange%20mint%20green%20and%20cream%20palette%2C%20minimal%20editorial%20vector%20style&width=1000&height=750&seq=gt-about-hero-01&orientation=landscape"
                alt="瓜田笔记编辑部场景插画，记录校园故事的工作台"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="我们的定位"
              title="一个不端着的校园故事集"
              desc="我们不假装权威，也不想当老师。这里更像是一个大家愿意坐下来聊两句的地方，热闹但不吵，真诚但不煽情。"
            />
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 lg:col-span-7">
            {[
              { value: `${stories.length * 37}+`, label: '已收录故事', note: '并且还在持续增加' },
              { value: '5', label: '校园场景分类', note: '覆盖大部分校园日常' },
              { value: '每周 5', label: '更新频次', note: '工作日准时与你见面' },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                <p className="font-heading text-3xl font-bold text-primary-600">{item.value}</p>
                <p className="mt-2 font-heading text-sm font-bold text-foreground-950">{item.label}</p>
                <p className="mt-1 text-xs text-foreground-500">{item.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-100/40 py-14 md:py-20">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <SectionHeading
            eyebrow="内容原则"
            title="我们坚持的四条底线"
            desc="校园故事最怕变成伤害别人的谈资，所以在动笔之前，这四条原则我们会反复确认。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item) => (
              <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <SectionHeading eyebrow="发展历程" title="从一个宿舍到一群瓜友" />
          <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((m) => (
              <li key={m.year} className="relative rounded-lg border border-background-200 bg-background-100/50 p-6">
                <span className="inline-flex items-center justify-center rounded-full bg-primary-500 px-3 py-1 font-heading text-xs font-bold text-background-50">
                  {m.year}
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{m.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-background-200 bg-foreground-950 py-14 text-background-100 md:py-20">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/20 px-3 py-1 text-xs font-medium text-background-200">
            <i className="ri-team-line" />
            编辑部
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-3xl">三个把小事当回事的人</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {team.map((member) => {
              const tone = toneMap[member.tone] ?? toneMap.primary;
              return (
                <div key={member.name} className="rounded-lg border border-background-50/10 bg-background-50/5 p-6">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-md ${tone.bg} ${tone.text} font-heading text-lg font-bold`}>
                    {member.name.slice(0, 1)}
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-background-50">{member.name}</h3>
                  <p className="mt-1 text-xs text-accent-300">{member.role}</p>
                  <p className="mt-3 text-sm leading-relaxed text-background-300">{member.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <SectionHeading
            eyebrow="用户口碑"
            title="瓜友们留下的只言片语"
            desc="这些话我们没做任何修改，它们也是编辑部继续更新的理由。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-100/50 p-6">
                <div className="flex items-center gap-1 text-secondary-500">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <i key={s} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.text}</blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-4">
                  <strong className="block text-sm font-medium text-foreground-900">{item.name}</strong>
                  <span className="text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-2">
            <div>
              <SectionHeading
                eyebrow="联系我们"
                title="想聊聊，随时都行"
                desc="无论是投稿、合作，还是对内容有疑问，写信给我们就好。工作日的邮件通常会在两天内收到回复。"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line" />
                </span>
                <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">读者来信</h3>
                <a href={`mailto:${siteInfo.email}`} className="mt-1 block break-all text-sm text-primary-600 hover:underline">
                  {siteInfo.email}
                </a>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-quill-pen-line" />
                </span>
                <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">故事投稿</h3>
                <a href={`mailto:${siteInfo.submitEmail}`} className="mt-1 block break-all text-sm text-primary-600 hover:underline">
                  {siteInfo.submitEmail}
                </a>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-wechat-line" />
                </span>
                <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">读者群</h3>
                <p className="mt-1 text-sm text-foreground-600">微信搜索 {siteInfo.contactWechat}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-time-line" />
                </span>
                <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">回复时间</h3>
                <p className="mt-1 text-sm text-foreground-600">工作日 10:00 – 19:00</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}